import { useState } from "react";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import { OnboardingInformationList } from "@/onboarding/simple-onboarding/onboarding-information-list";
import { useOnboardingNavigation } from "@/onboarding/simple-onboarding/shared-query";
import { StepLayout } from "@/onboarding/simple-onboarding/step-layout";
import type {
  BusinessOnboarding,
  IntakeStep,
} from "@/onboarding/simple-onboarding/types";
import { ReviewConfirmationCard } from "./review-confirmation-card";
import { ReviewNavigation } from "./review-navigation";

export const ReviewSubmitScreen = ({
  business,
  state,
}: {
  business: Pick<IEnrichedBusiness, "id" | "name" | "time_zone_label">;
  state: BusinessOnboarding;
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const mutation = useOnboardingNavigation(business.id);
  const edit = (step: IntakeStep) =>
    mutation.mutate({ step: "onboarding_call", direction: "jump", to: step });

  return (
    <StepLayout
      activePane="call"
      showChecklist={false}
      title="Review and submit"
      subtitle="Check your information. Select a step to make a correction before you submit."
      nav={
        <ReviewNavigation
          confirmed={confirmed}
          pending={mutation.isPending}
          error={
            mutation.isError
              ? extractErrorMessage(mutation.error).message
              : undefined
          }
          onNavigate={(direction) =>
            mutation.mutate({
              step: "onboarding_call",
              direction,
              ...(direction === "next"
                ? { answers: { information_confirmed: confirmed } }
                : {}),
            })
          }
        />
      }
    >
      <OnboardingInformationList
        state={state}
        companyName={business.name}
        timeZone={business.time_zone_label}
        onEdit={edit}
        pending={mutation.isPending}
      />
      <ReviewConfirmationCard
        confirmed={confirmed}
        disabled={mutation.isPending}
        onConfirmedChange={setConfirmed}
      />
    </StepLayout>
  );
};
